import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Popup from "components/popup/Popup";
import { ITable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import Divider from "components/divider";
import "./openTablesWarningPopup.scss";

interface IProps {
  open: boolean;
  tables: ITable[];
  onCancel: () => void;
  onContinue: () => void;
  continueText?: string;
}

export default function OpenTablesWarningPopup(props: IProps) {
  const { open, tables, onCancel, onContinue, continueText } = props;
  const { t } = useTranslation();
  const openTablesSummary =
    tables.length === 1
      ? t("1 table requires attention before you print your shift summary.")
      : t("{{count}} tables require attention before you print your shift summary.", { count: tables.length });

  const displayTables = useMemo(
    () =>
      [...tables].sort((tableA, tableB) =>
        (tableA.title || tableA.short_title || "").localeCompare(tableB.title || tableB.short_title || ""),
      ),
    [tables],
  );

  return (
    <Popup
      open={open}
      type="warning"
      title={t("Open Tables")}
      description={t("The following tables are still open under your name.")}
      onCancel={onCancel}
      onOk={onContinue}
      cancelText={t("Cancel")}
      okText={continueText ?? t("Continue")}
      stacked
      aligned="left"
      noFixedWidth
    >
      <div className="open-tables-warning-popup">
        <p className="open-tables-warning-popup__summary">
          {openTablesSummary}
        </p>

        <div className="open-tables-warning-popup__list">
          {displayTables.map((table, index) => {
            const tableTitle = table.title || table.short_title || t("Untitled Table");

            return (
              <React.Fragment key={table.id}>
                {index > 0 ? <Divider small /> : null}

                <div className="open-tables-warning-popup__item">
                  <p className="open-tables-warning-popup__item-title">{tableTitle}</p>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Popup>
  );
}
